import React, { Component } from "react";
import {
  Header,
  Table,
  Button,
  Checkbox,
  Message,
  Loader,
  Icon,
  Popup,
  Grid,
  Modal,
  Segment,
  Card,
} from "semantic-ui-react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { weededDocumentsApi } from "@api/weededDocuments";
import { YearPicker } from "@components/YearPicker";
import { ExportSearchResults } from "@components/backoffice/ExportSearchResults";
import {
  sendSuccessNotification,
  sendErrorNotification,
} from "@components/Notifications";
import { generateExportHTML } from "@components/HtmlExportTemplate/htmlExportTemplate";
import { NewButton } from "@components/backoffice/buttons/NewButton";
import DeleteButton from "@components/backoffice/DeleteRecordModal/DeleteButton/DeleteButton";

class WeededDocumentList extends Component {
  state = {
    weededDocuments: [],
    filteredDocuments: [],
    selectedDocuments: [],
    loading: true,
    error: null,
    operationLoading: false,
    deleteModalOpen: false,
    pendingDeleteIds: [],
    fromYear: "",
    toYear: "",
    exportLoading: false,
  };

  async componentDidMount() {
    await this.loadWeededDocuments();
  }

  loadWeededDocuments = async () => {
    try {
      this.setState({ loading: true, error: null });
      const response = await weededDocumentsApi.getWeededDocuments();
      this.setState(
        {
          weededDocuments: response.weeded_documents || [],
          loading: false,
        },
        this.applyFilters
      );
    } catch (error) {
      this.setState({
        error: "Failed to load weeded documents",
        loading: false,
      });
      this.props.dispatch(
        sendErrorNotification(
          "Loading failed",
          "Failed to load weeded documents. Please try again."
        )
      );
    }
  };

  applyFilters = () => {
    const { weededDocuments, fromYear, toYear } = this.state;
    let filtered = [...weededDocuments];

    if (fromYear || toYear) {
      filtered = filtered.filter((doc) => {
        if (!doc.weedout_date) return false;
        const docYear = new Date(doc.weedout_date).getFullYear();
        if (fromYear && docYear < parseInt(fromYear)) return false;
        if (toYear && docYear > parseInt(toYear)) return false;
        return true;
      });
    }

    this.setState({ filteredDocuments: filtered, selectedDocuments: [] });
  };

  handleFromYearChange = (value) =>
    this.setState({ fromYear: value }, this.applyFilters);
  handleToYearChange = (value) =>
    this.setState({ toYear: value }, this.applyFilters);
  clearFilters = () =>
    this.setState({ fromYear: "", toYear: "" }, this.applyFilters);

  handleExport = async (format, maxResults = null) => {
    const { filteredDocuments } = this.state;

    try {
      this.setState({ exportLoading: true });
      const documentsToExport = maxResults
        ? filteredDocuments.slice(0, maxResults)
        : filteredDocuments;

      if (documentsToExport.length === 0) {
        this.props.dispatch(
          sendErrorNotification(
            "No data to export",
            "There are no documents to export with the current filters."
          )
        );
        return;
      }

      if (format === "csv") {
        this.exportToCSV(documentsToExport);
      } else if (format === "pdf") {
        this.customExportPDF(documentsToExport);
      }
    } catch (error) {
      this.props.dispatch(
        sendErrorNotification(
          "Export failed",
          "Failed to export documents. Please try again."
        )
      );
    } finally {
      this.setState({ exportLoading: false });
    }
  };

  customExportPDF = async (documents) => {
    try {
      const desiredColumns = [
        "Title",
        "Authors",
        "PID",
        "Weeded Date",
        "Weeded By",
      ];
      const tableHeaders = desiredColumns.map((h) => `<th>${h}</th>`).join("");

      const tableRows = documents
        .map((doc) => {
          const cells = [
            `<td>${doc.title || "Untitled"}</td>`,
            `<td>${this.formatAuthors(doc.authors)}</td>`,
            `<td>${doc.pid || "N/A"}</td>`,
            `<td>${this.formatDate(doc.weedout_date)}</td>`,
            `<td>${doc.weeded_by || "Unknown"}</td>`,
          ].join("");
          return `<tr>${cells}</tr>`;
        })
        .join("");

      const htmlContent = generateExportHTML({
        reportTitle: "Weeded Documents Export Report",
        generatedDate: new Date().toLocaleString(),
        tableHeaders,
        tableRows,
      });

      const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, "_blank");

      setTimeout(() => URL.revokeObjectURL(url), 2000);
      if (printWindow) {
        printWindow.onload = () => setTimeout(() => printWindow.focus(), 500);
      }

      this.props.dispatch(
        sendSuccessNotification(
          "PDF Export ready",
          `Successfully prepared ${documents.length} weeded documents for PDF export. Use browser's print function to save as PDF.`
        )
      );
    } catch (error) {
      this.props.dispatch(
        sendErrorNotification(
          "PDF Export failed",
          "Failed to generate PDF export. Please try again."
        )
      );
    }
  };

  exportToCSV = (documents) => {
    const headers = ["Title", "PID", "Authors", "Weeded Date", "Weeded By"];
    const csvContent = [
      headers.join(","),
      ...documents.map((doc) =>
        [
          `"${(doc.title || "Untitled").replace(/"/g, '""')}"`,
          `"${doc.pid || ""}"`,
          `"${this.formatAuthors(doc.authors).replace(/"/g, '""')}"`,
          `"${this.formatDate(doc.weedout_date)}"`,
          `"${doc.weeded_by || "Unknown"}"`,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `weeded_documents_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    this.props.dispatch(
      sendSuccessNotification(
        "Export successful",
        `Successfully exported ${documents.length} documents to CSV.`
      )
    );
  };

  handleSelectDocument = (documentId, isChecked) => {
    this.setState((prevState) => ({
      selectedDocuments: isChecked
        ? [...prevState.selectedDocuments, documentId]
        : prevState.selectedDocuments.filter((id) => id !== documentId),
    }));
  };

  handleSelectAll = (isChecked) => {
    this.setState((prevState) => ({
      selectedDocuments: isChecked
        ? prevState.filteredDocuments.map((doc) => doc.id)
        : [],
    }));
  };

  cleanIds = (ids) =>
    ids.map((id) => {
      if (typeof id === "object" && id !== null) {
        return id.id || id.document_id || id.pid || String(id);
      }
      return id;
    });

  handleRestoreDocuments = async (documentIds = null) => {
    const idsToRestore = documentIds || [...this.state.selectedDocuments];

    if (!idsToRestore.length) {
      this.props.dispatch(
        sendErrorNotification(
          "No selection",
          "Please select documents to restore"
        )
      );
      return;
    }

    try {
      this.setState({ operationLoading: true });
      const response = await weededDocumentsApi.restoreDocuments(
        this.cleanIds(idsToRestore)
      );

      if (response.restored_count > 0) {
        this.props.dispatch(
          sendSuccessNotification(
            "Success!",
            `Successfully restored ${response.restored_count} document${
              response.restored_count > 1 ? "s" : ""
            } to the catalog`
          )
        );
        this.setState({ selectedDocuments: [] });
        setTimeout(this.loadWeededDocuments, 500);
      } else {
        this.props.dispatch(
          sendErrorNotification(
            "Restore failed",
            "No documents were restored. Please check the logs for details."
          )
        );
      }
    } catch (error) {
      this.props.dispatch(
        sendErrorNotification(
          "Restore failed",
          error.response?.data?.message ||
            "Failed to restore documents. Please try again."
        )
      );
    } finally {
      this.setState({ operationLoading: false });
    }
  };

  handleDeleteDocuments = (documentIds = null) => {
    const idsToDelete = documentIds || [...this.state.selectedDocuments];

    if (!idsToDelete.length) {
      this.props.dispatch(
        sendErrorNotification(
          "No selection",
          "Please select documents to delete"
        )
      );
      return;
    }

    this.setState({
      deleteModalOpen: true,
      pendingDeleteIds: this.cleanIds(idsToDelete),
    });
  };

  confirmDelete = async () => {
    const { pendingDeleteIds } = this.state;

    try {
      this.setState({ operationLoading: true, deleteModalOpen: false });
      const response = await weededDocumentsApi.deleteDocuments(
        pendingDeleteIds
      );

      if (response.deleted_count > 0) {
        this.props.dispatch(
          sendSuccessNotification(
            "Success!",
            `Successfully deleted ${response.deleted_count} document${
              response.deleted_count > 1 ? "s" : ""
            } permanently`
          )
        );
        this.setState({ selectedDocuments: [] });
        setTimeout(this.loadWeededDocuments, 500);
      } else {
        this.props.dispatch(
          sendErrorNotification(
            "Delete failed",
            response.message ||
              "No documents were deleted. Please check the logs for details."
          )
        );
      }
    } catch (error) {
      this.props.dispatch(
        sendErrorNotification(
          "Delete failed",
          error.response?.data?.message ||
            "Failed to delete documents. Please try again."
        )
      );
    } finally {
      this.setState({ operationLoading: false, pendingDeleteIds: [] });
    }
  };

  formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  formatAuthors = (authors) => {
    if (!authors || authors.length === 0) return "N/A";

    const authorNames = authors
      .slice(0, 3)
      .map(
        (author) =>
          author.full_name ||
          `${author.first_name || ""} ${author.last_name || ""}`.trim()
      )
      .filter((name) => name.trim() !== "");

    if (authorNames.length === 0) return "N/A";
    if (authors.length > 3) authorNames.push("et al.");
    return authorNames.join(", ");
  };

  renderYearFilter = () => {
    const { fromYear, toYear } = this.state;
    const hasFilters = fromYear || toYear;

    return (
      <Card>
        <Card.Content>
          <Card.Header>Weeded Year</Card.Header>
          <Card.Meta>
            Filter documents by the year they were weeded out
          </Card.Meta>
        </Card.Content>
        <Card.Content>
          <YearPicker
            defaultValue={fromYear}
            placeholder="From Year"
            handleYearChange={this.handleFromYearChange}
            name="fromYear"
          />
        </Card.Content>
        <Card.Content>
          <YearPicker
            defaultValue={toYear}
            placeholder="To Year"
            handleYearChange={this.handleToYearChange}
            name="toYear"
          />
        </Card.Content>
        {hasFilters && (
          <Card.Content>
            <Button
              size="small"
              onClick={this.clearFilters}
              icon="close"
              content="Clear Filters"
            />
          </Card.Content>
        )}
      </Card>
    );
  };

  render() {
    const {
      weededDocuments,
      filteredDocuments,
      selectedDocuments,
      loading,
      error,
      operationLoading,
      deleteModalOpen,
      pendingDeleteIds,
      fromYear,
      toYear,
      exportLoading,
    } = this.state;

    const documentsToShow = filteredDocuments;
    const hasFilters = fromYear || toYear;

    if (loading) {
      return (
        <Segment placeholder>
          <Header icon>
            <Icon name="search" />
            <Loader active inline="centered">
              Loading weeded documents...
            </Loader>
          </Header>
        </Segment>
      );
    }

    if (error) {
      return (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
          <Button onClick={this.loadWeededDocuments}>Try Again</Button>
        </Message>
      );
    }

    const allSelected =
      selectedDocuments.length === documentsToShow.length &&
      documentsToShow.length > 0;
    const someSelected = selectedDocuments.length > 0;

    return (
      <>
        <Header as="h3">
          Weeded Documents
          <Header.Subheader>
            {hasFilters ? (
              <>
                Showing {documentsToShow.length} of {weededDocuments.length}{" "}
                {documentsToShow.length === 1 ? "document" : "documents"}
                {fromYear && toYear
                  ? ` weeded between ${fromYear} and ${toYear}`
                  : fromYear
                  ? ` weeded from ${fromYear} onwards`
                  : toYear
                  ? ` weeded up to ${toYear}`
                  : ""}
              </>
            ) : (
              <>
                {weededDocuments.length}{" "}
                {weededDocuments.length === 1 ? "document" : "documents"} in
                weeding queue
              </>
            )}
          </Header.Subheader>
        </Header>

        <Grid>
          <Grid.Row>
            <Grid.Column width={3}>{this.renderYearFilter()}</Grid.Column>
            <Grid.Column width={13}>
              {documentsToShow.length > 0 && (
                <Grid>
                  <Grid.Row>
                    <Grid.Column width={8}>
                      <NewButton
                        icon="undo"
                        text={`Restore Selected (${selectedDocuments.length})`}
                        positive
                        disabled={!someSelected || operationLoading}
                        loading={operationLoading}
                        onClick={() => this.handleRestoreDocuments()}
                        style={{ marginRight: "8px" }}
                      />
                      <DeleteButton
                        text={`Delete Permanently (${selectedDocuments.length})`}
                        disabled={!someSelected || operationLoading}
                        loading={operationLoading}
                        onClick={() => this.handleDeleteDocuments()}
                      />
                    </Grid.Column>
                    <Grid.Column width={8} textAlign="right">
                      <ExportSearchResults
                        onExportClick={this.handleExport}
                        isLoading={exportLoading}
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              )}

              {documentsToShow.length === 0 ? (
                <Message info>
                  <Message.Header>
                    {hasFilters
                      ? "No Documents Match Filters"
                      : "No Weeded Documents"}
                  </Message.Header>
                  <p>
                    {hasFilters
                      ? "No documents match the selected year range. Try adjusting your filters."
                      : "No documents are currently in the weeding queue."}
                  </p>
                  {!hasFilters && (
                    <p>
                      Documents appear here after being weeded out from the
                      catalog. From here, you can restore them back to the
                      catalog or delete them permanently.
                    </p>
                  )}
                </Message>
              ) : (
                <Table celled striped>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell width={1}>
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected && !allSelected}
                          onChange={(e, { checked }) =>
                            this.handleSelectAll(checked)
                          }
                        />
                      </Table.HeaderCell>
                      <Table.HeaderCell width={4}>Title</Table.HeaderCell>
                      <Table.HeaderCell width={3}>Authors</Table.HeaderCell>
                      <Table.HeaderCell width={2}>Weeded Date</Table.HeaderCell>
                      <Table.HeaderCell width={2}>Weeded By</Table.HeaderCell>
                      <Table.HeaderCell width={2}>Actions</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {documentsToShow.map((doc) => (
                      <Table.Row key={doc.id}>
                        <Table.Cell>
                          <Checkbox
                            checked={selectedDocuments.includes(doc.id)}
                            onChange={(e, { checked }) =>
                              this.handleSelectDocument(doc.id, checked)
                            }
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <strong>{doc.title || "Untitled"}</strong>
                          {doc.pid && (
                            <div style={{ fontSize: "0.9em", color: "#666" }}>
                              PID: {doc.pid}
                            </div>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {this.formatAuthors(doc.authors)}
                        </Table.Cell>
                        <Table.Cell>
                          {this.formatDate(doc.weedout_date)}
                        </Table.Cell>
                        <Table.Cell>{doc.weeded_by || "Unknown"}</Table.Cell>
                        <Table.Cell>
                          <Popup
                            content="Restore to catalog"
                            trigger={
                              <Button
                                icon="undo"
                                positive
                                size="mini"
                                onClick={() =>
                                  this.handleRestoreDocuments([doc.id])
                                }
                                disabled={operationLoading}
                              />
                            }
                          />
                          <Popup
                            content="Delete permanently"
                            trigger={
                              <Button
                                icon="trash"
                                negative
                                size="mini"
                                onClick={() =>
                                  this.handleDeleteDocuments([doc.id])
                                }
                                disabled={operationLoading}
                              />
                            }
                          />
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <Modal
          open={deleteModalOpen}
          onClose={() => this.setState({ deleteModalOpen: false })}
          size="small"
        >
          <Modal.Header>
            <Icon name="warning sign" />
            Confirm Permanent Deletion
          </Modal.Header>
          <Modal.Content>
            <p>
              Are you sure you want to permanently delete{" "}
              {pendingDeleteIds.length} selected{" "}
              {pendingDeleteIds.length === 1 ? "document" : "documents"}?
            </p>
            <p>
              <strong>This action cannot be undone.</strong>
            </p>
          </Modal.Content>
          <Modal.Actions>
            <Button
              onClick={() =>
                this.setState({ deleteModalOpen: false, pendingDeleteIds: [] })
              }
            >
              <Icon name="cancel" />
              Cancel
            </Button>
            <Button
              negative
              onClick={this.confirmDelete}
              loading={operationLoading}
            >
              <Icon name="trash" />
              Delete Permanently
            </Button>
          </Modal.Actions>
        </Modal>
      </>
    );
  }
}

WeededDocumentList.propTypes = {
  dispatch: PropTypes.func.isRequired,
};

export default connect()(WeededDocumentList);
